/**
 * AGROMX Frontend - Persona Form View
 * Formulario para crear/editar personas
 */

import personaService from '../../services/persona.service.js';
import permisos from '../../services/permissions.service.js';
import router from '../../router/router.js';
import modal from '../../utils/modal.js';

export default {
  data: {
    persona: null,
    isEditing: false,
    loading: false,
    rolPreseleccionado: null
  },

  async render() {
    // Detectar si es edición o creación
    const path = window.location.pathname;
    const editMatch = path.match(/\/catalogos\/personas\/(\d+)\/editar/);
    const isEditing = !!editMatch;
    this.data.isEditing = isEditing;

    // Obtener rol preseleccionado de URL (usando search, no hash)
    const urlParams = new URLSearchParams(window.location.search);
    this.data.rolPreseleccionado = urlParams.get('rol');

    if (isEditing) {
      const personaId = editMatch[1];
      try {
        this.data.persona = await personaService.getById(personaId);
      } catch (error) {
        console.error('Error cargando persona:', error);
        window.toast.error('Error al cargar los datos');
        router.navigate('/catalogos/personas');
        return '<div>Error al cargar datos</div>';
      }
    } else {
      // Limpiar datos previos al crear (el módulo es singleton, persiste entre navegaciones)
      this.data.persona = {};
    }

    // Título dinámico según rol preseleccionado
    const titulos = {
      cliente: 'Nuevo Cliente',
      proveedor: 'Nuevo Proveedor',
      responsable: 'Nuevo Responsable',
      distribuidor: 'Nuevo Distribuidor',
      agente_aduanal: 'Nuevo Agente Aduanal'
    };
    const titulo = isEditing ? 'Editar Persona' : (titulos[this.data.rolPreseleccionado] || 'Nueva Persona');
    const roles = personaService.getRoles();
    const estados = personaService.getEstadosMexico();
    const areas = personaService.getAreasResponsable();

    const estadosOptions = estados.map(e =>
      `<option value="${e}" ${this.data.persona?.estado === e ? 'selected' : ''}>${e}</option>`
    ).join('');

    const areasOptions = areas.map(a =>
      `<option value="${a.value}">${a.label}</option>`
    ).join('');

    const p = this.data.persona || {};

    return `
      <div class="page-header">
        <h1>${titulo}</h1>
        <button class="btn btn-secondary" id="btnVolver">
          Volver
        </button>
      </div>

      <form id="personaForm">
        <!-- Tipo de Persona -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Tipo de Persona</h3>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Tipo de Persona <span style="color: var(--danger);">*</span></label>
              <select id="tipoPersona" class="form-control" required ${isEditing ? 'disabled' : ''}>
                <option value="">Seleccionar...</option>
                <option value="fisica" ${p.tipo_persona === 'fisica' ? 'selected' : ''}>Persona Física</option>
                <option value="moral" ${p.tipo_persona === 'moral' ? 'selected' : ''}>Persona Moral (Empresa)</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Estado</label>
              <select id="inputActivo" class="form-control">
                <option value="1" ${p.activo !== 0 ? 'selected' : ''}>Activo</option>
                <option value="0" ${p.activo === 0 ? 'selected' : ''}>Inactivo</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Datos Generales -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Datos Generales</h3>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Nombre Completo / Razón Social <span style="color: var(--danger);">*</span></label>
              <input
                type="text"
                id="inputNombreCompleto"
                class="form-control"
                value="${p.nombre_completo || ''}"
                placeholder="Nombre completo o razón social"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">Nombre Comercial</label>
              <input
                type="text"
                id="inputNombreComercial"
                class="form-control"
                value="${p.nombre_comercial || ''}"
                placeholder="Nombre comercial (opcional)"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">RFC</label>
              <input
                type="text"
                id="inputRfc"
                class="form-control"
                value="${p.rfc || ''}"
                placeholder="RFC (13 caracteres)"
                maxlength="13"
                style="text-transform: uppercase;"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Email <span class="text-muted" style="font-weight:400;">(opcional)</span></label>
              <input
                type="email"
                id="inputEmail"
                class="form-control"
                value="${p.email || ''}"
                placeholder="correo@ejemplo.com (opcional)"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Teléfono <span style="color: var(--danger);">*</span></label>
              <input
                type="tel"
                id="inputTelefono"
                class="form-control"
                value="${p.telefono || ''}"
                placeholder="5512345678"
                inputmode="numeric"
                pattern="\\d{10}"
                maxlength="10"
                oninput="this.value = this.value.replace(/\\D/g, '')"
                title="10 dígitos numéricos"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">Celular</label>
              <input
                type="tel"
                id="inputCelular"
                class="form-control"
                value="${p.celular || ''}"
                placeholder="5512345678"
                inputmode="numeric"
                pattern="\\d{10}"
                maxlength="10"
                oninput="this.value = this.value.replace(/\\D/g, '')"
                title="10 dígitos numéricos"
              />
            </div>
            <div class="form-group">
              <label class="form-label">WhatsApp</label>
              <input
                type="tel"
                id="inputWhatsapp"
                class="form-control"
                value="${p.whatsapp || ''}"
                placeholder="5512345678"
                inputmode="numeric"
                pattern="\\d{10}"
                maxlength="10"
                oninput="this.value = this.value.replace(/\\D/g, '')"
                title="10 dígitos numéricos"
              />
            </div>
          </div>
        </div>

        <!-- Dirección -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Dirección</h3>
          </div>
          <div class="form-row">
            <div class="form-group" style="flex: 2;">
              <label class="form-label">Dirección <span style="color: var(--danger);">*</span></label>
              <input
                type="text"
                id="inputDireccion"
                class="form-control"
                value="${p.direccion || ''}"
                placeholder="Calle, número, interior"
                required
              />
            </div>
            <div class="form-group">
              <label class="form-label">Colonia</label>
              <input
                type="text"
                id="inputColonia"
                class="form-control"
                value="${p.colonia || ''}"
                placeholder="Colonia"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Ciudad</label>
              <input
                type="text"
                id="inputCiudad"
                class="form-control"
                value="${p.ciudad || ''}"
                placeholder="Ciudad"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Estado</label>
              <select id="inputEstado" class="form-control">
                <option value="">Seleccionar estado...</option>
                ${estadosOptions}
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">C.P.</label>
              <input
                type="text"
                id="inputCodigoPostal"
                class="form-control"
                value="${p.codigo_postal || ''}"
                placeholder="00000"
                maxlength="5"
              />
            </div>
          </div>
        </div>

        <!-- Detalles Persona Física -->
        <div class="card" id="seccionPersonaFisica" style="display: none;">
          <div class="card-header">
            <h3 class="card-title">Datos de Persona Física</h3>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Nombre(s) <span style="color: var(--danger);">*</span></label>
              <input
                type="text"
                id="inputPfNombre"
                class="form-control"
                value="${p.detalles?.nombre || ''}"
                placeholder="Nombre(s)"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Apellido Paterno <span style="color: var(--danger);">*</span></label>
              <input
                type="text"
                id="inputPfApellidoPaterno"
                class="form-control"
                value="${p.detalles?.apellido_paterno || ''}"
                placeholder="Apellido paterno"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Apellido Materno <span style="color: var(--danger);">*</span></label>
              <input
                type="text"
                id="inputPfApellidoMaterno"
                class="form-control"
                value="${p.detalles?.apellido_materno || ''}"
                placeholder="Apellido materno"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">CURP</label>
              <input
                type="text"
                id="inputPfCurp"
                class="form-control"
                value="${p.detalles?.curp || ''}"
                placeholder="CURP (18 caracteres)"
                maxlength="18"
                style="text-transform: uppercase;"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Fecha de Nacimiento</label>
              <input
                type="date"
                id="inputPfFechaNacimiento"
                class="form-control"
                value="${p.detalles?.fecha_nacimiento ? p.detalles.fecha_nacimiento.split('T')[0] : ''}"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Tipo de Identificación</label>
              <select id="inputPfTipoId" class="form-control">
                <option value="">Seleccionar...</option>
                <option value="INE" ${p.detalles?.tipo_identificacion === 'INE' ? 'selected' : ''}>INE</option>
                <option value="Pasaporte" ${p.detalles?.tipo_identificacion === 'Pasaporte' ? 'selected' : ''}>Pasaporte</option>
                <option value="Licencia" ${p.detalles?.tipo_identificacion === 'Licencia' ? 'selected' : ''}>Licencia de conducir</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Número de Identificación</label>
              <input
                type="text"
                id="inputPfNumeroId"
                class="form-control"
                value="${p.detalles?.numero_identificacion || ''}"
                placeholder="Número de identificación"
              />
            </div>
          </div>
        </div>

        <!-- Detalles Persona Moral -->
        <div class="card" id="seccionPersonaMoral" style="display: none;">
          <div class="card-header">
            <h3 class="card-title">Datos de Persona Moral</h3>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Razón Social <span style="color: var(--danger);">*</span></label>
              <input
                type="text"
                id="inputPmRazonSocial"
                class="form-control"
                value="${p.detalles?.razon_social || ''}"
                placeholder="Razón social completa"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Fecha de Constitución</label>
              <input
                type="date"
                id="inputPmFechaConstitucion"
                class="form-control"
                value="${p.detalles?.fecha_constitucion ? p.detalles.fecha_constitucion.split('T')[0] : ''}"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Representante Legal</label>
              <input
                type="text"
                id="inputPmRepresentante"
                class="form-control"
                value="${p.detalles?.representante_legal || ''}"
                placeholder="Nombre del representante legal"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Email del Representante</label>
              <input
                type="email"
                id="inputPmEmailRepresentante"
                class="form-control"
                value="${p.detalles?.email_representante || ''}"
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Teléfono del Representante</label>
              <input
                type="tel"
                id="inputPmTelefonoRepresentante"
                class="form-control"
                value="${p.detalles?.telefono_representante || ''}"
                placeholder="(55) 1234-5678"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Régimen Fiscal</label>
              <input
                type="text"
                id="inputPmRegimenFiscal"
                class="form-control"
                value="${p.detalles?.regimen_fiscal || ''}"
                placeholder="Ej: General de Ley Personas Morales"
              />
            </div>
          </div>
        </div>

        <!-- Roles: oculto si hay rol preseleccionado (?rol=); visible en vista genérica Personas -->
        ${this.data.rolPreseleccionado ? (isEditing ? '' : `
          <!-- Rol preseleccionado (oculto) -->
          <input type="hidden" name="roles" value="${this.data.rolPreseleccionado}" id="rolPreseleccionado" />

          ${this.data.rolPreseleccionado === 'responsable' ? `
          <!-- Configuración específica para Responsable -->
          <div class="card">
            <div class="card-header">
              <h3 class="card-title">Configuración de Responsable</h3>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Puesto</label>
                <input
                  type="text"
                  id="inputResponsablePuesto"
                  class="form-control"
                  placeholder="Ej: Inspector de Calidad"
                />
              </div>
              <div class="form-group">
                <label class="form-label">Área</label>
                <select id="inputResponsableArea" class="form-control">
                  <option value="">Seleccionar área...</option>
                  ${areasOptions}
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>
                  <input type="checkbox" id="inputPuedeFirmarLiberaciones" />
                  Puede firmar liberaciones
                </label>
              </div>
              <div class="form-group">
                <label>
                  <input type="checkbox" id="inputPuedeAprobarOT" />
                  Puede aprobar Órdenes de Trabajo
                </label>
              </div>
            </div>
          </div>
          ` : ''}
        `) : `
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Roles</h3>
          </div>
          <div id="rolesContainer">
            ${this.renderRolesCheckboxes(p.roles || [])}
          </div>

          <!-- Configuración específica para Responsable -->
          <div id="configResponsable" style="display: none; margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--gray-200);">
            <h4 style="margin-bottom: 1rem;">Configuración de Responsable</h4>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Puesto</label>
                <input
                  type="text"
                  id="inputResponsablePuesto"
                  class="form-control"
                  placeholder="Ej: Inspector de Calidad"
                />
              </div>
              <div class="form-group">
                <label class="form-label">Área</label>
                <select id="inputResponsableArea" class="form-control">
                  <option value="">Seleccionar área...</option>
                  ${areasOptions}
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>
                  <input type="checkbox" id="inputPuedeFirmarLiberaciones" />
                  Puede firmar liberaciones
                </label>
              </div>
              <div class="form-group">
                <label>
                  <input type="checkbox" id="inputPuedeAprobarOT" />
                  Puede aprobar Órdenes de Trabajo
                </label>
              </div>
            </div>
          </div>
        </div>
        `}

        <!-- Notas -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Notas</h3>
          </div>
          <div class="form-group">
            <textarea
              id="inputNotas"
              class="form-control"
              rows="3"
              placeholder="Notas adicionales..."
            >${p.notas || ''}</textarea>
          </div>
        </div>

        <!-- Botones de acción -->
        <div class="card" style="display: flex; flex-direction: row; gap: 1rem; justify-content: flex-end;">
          <button type="button" class="btn btn-secondary" id="btnCancelar">Cancelar</button>
          <button type="submit" class="btn btn-primary" id="btnGuardar">
            ${isEditing ? 'Guardar Cambios' : 'Crear Persona'}
          </button>
        </div>
      </form>
    `;
  },

  renderRolesCheckboxes(rolesActuales) {
    const roles = personaService.getRoles();
    const rolesActivos = rolesActuales
      .filter(r => r.activo)
      .map(r => r.rol);

    return `
      <div class="form-row" style="gap: 2rem;">
        ${roles.map(rol => `
          <div class="form-group" style="flex: 0;">
            <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;">
              <input
                type="checkbox"
                name="roles"
                value="${rol.value}"
                ${rolesActivos.includes(rol.value) || this.data.rolPreseleccionado === rol.value ? 'checked' : ''}
              />
              ${rol.label}
            </label>
          </div>
        `).join('')}
      </div>
    `;
  },

  // Mapea el rol preseleccionado a la clave del módulo en el sistema de permisos
  _getModuloKey() {
    const map = {
      cliente: 'clientes',
      responsable: 'responsables',
      distribuidor: 'distribuidores',
      agente_aduanal: 'agentes_aduanales'
    };
    return map[this.data.rolPreseleccionado] || 'personas';
  },

  async afterRender() {
    const moduloKey = this._getModuloKey();
    const accionRequerida = this.data.isEditing ? 'editar' : 'crear';
    if (!permisos.can(moduloKey, accionRequerida)) {
      if (permisos.primeraRuta) { router.navigate(permisos.primeraRuta); }
      else { document.getElementById('content').innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:60vh;"><div style="text-align:center;color:var(--primary);"><h2>Acceso denegado</h2><p>No tienes permisos para esta acción.</p></div></div>'; }
      return;
    }
    window.personaFormView = this;
    this.setupEventListeners();
    this.actualizarSeccionesTipoPersona();
    this.actualizarConfigResponsable();
  },

  setupEventListeners() {
    // Volver
    document.getElementById('btnVolver')?.addEventListener('click', () => {
      this.volver();
    });
    document.getElementById('btnCancelar')?.addEventListener('click', () => {
      this.volver();
    });

    // Cambio de tipo de persona
    document.getElementById('tipoPersona')?.addEventListener('change', () => {
      this.actualizarSeccionesTipoPersona();
    });

    // Cambio en roles (para mostrar/ocultar config de responsable)
    document.querySelectorAll('input[name="roles"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.actualizarConfigResponsable();
      });
    });

    // RFC mayúsculas
    document.getElementById('inputRfc')?.addEventListener('input', (e) => {
      e.target.value = e.target.value.toUpperCase();
    });

    // CURP mayúsculas
    document.getElementById('inputPfCurp')?.addEventListener('input', (e) => {
      e.target.value = e.target.value.toUpperCase();
    });

    // Submit
    document.getElementById('personaForm')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.guardar();
    });
  },

  actualizarSeccionesTipoPersona() {
    const tipo = document.getElementById('tipoPersona').value;
    const seccionFisica = document.getElementById('seccionPersonaFisica');
    const seccionMoral = document.getElementById('seccionPersonaMoral');

    seccionFisica.style.display = tipo === 'fisica' ? 'block' : 'none';
    seccionMoral.style.display = tipo === 'moral' ? 'block' : 'none';
  },

  actualizarConfigResponsable() {
    const checkboxResponsable = document.querySelector('input[name="roles"][value="responsable"]');
    const configResponsable = document.getElementById('configResponsable');

    if (configResponsable) {
      configResponsable.style.display = checkboxResponsable?.checked ? 'block' : 'none';
    }
  },

  volver() {
    const rolParam = this.data.rolPreseleccionado ? `?rol=${this.data.rolPreseleccionado}` : '';
    router.navigate(`/catalogos/personas${rolParam}`);
  },

  async guardar() {
    const btnGuardar = document.getElementById('btnGuardar');
    btnGuardar.disabled = true;
    btnGuardar.textContent = 'Guardando...';

    try {
      const formData = this.recopilarDatos();

      // Validaciones básicas
      if (!formData.tipo_persona) {
        throw new Error('Debe seleccionar el tipo de persona');
      }
      if (!formData.nombre_completo) {
        throw new Error('El nombre completo / razón social es obligatorio');
      }
      if (formData.tipo_persona === 'fisica') {
        if (!formData.persona_fisica?.nombre) {
          throw new Error('El nombre es obligatorio');
        }
        if (!formData.persona_fisica?.apellido_paterno) {
          throw new Error('El apellido paterno es obligatorio');
        }
        if (!formData.persona_fisica?.apellido_materno) {
          throw new Error('El apellido materno es obligatorio');
        }
      }
      if (formData.tipo_persona === 'moral') {
        if (!formData.persona_moral?.razon_social) {
          throw new Error('La razón social es obligatoria');
        }
      }
      // El email es OPCIONAL (agricultores sin correo). El teléfono sí es obligatorio.
      if (!formData.telefono) {
        throw new Error('El teléfono es obligatorio');
      }
      const telefonoRegex = /^\d{10}$/;
      if (!telefonoRegex.test(formData.telefono)) {
        throw new Error('El Teléfono debe ser de 10 dígitos numéricos');
      }
      if (formData.celular && !telefonoRegex.test(formData.celular)) {
        throw new Error('El Celular debe ser de 10 dígitos numéricos');
      }
      if (formData.whatsapp && !telefonoRegex.test(formData.whatsapp)) {
        throw new Error('El WhatsApp debe ser de 10 dígitos numéricos');
      }
      if (!formData.direccion) {
        throw new Error('La dirección es obligatoria');
      }

      let result;
      if (this.data.isEditing) {
        result = await personaService.update(this.data.persona.id, formData);
        window.toast.success('Persona actualizada exitosamente');
      } else {
        result = await personaService.create(formData);
        window.toast.success('Persona creada exitosamente');
      }

      this.volver();
    } catch (error) {
      // Modal simple para 409 con duplicados (sin exponer datos de otra persona)
      if (error.status === 409 && error.details?.campos_coincidentes?.length) {
        const campos = error.details.campos_coincidentes;
        // Construir mensaje según campo coincidente: "ese email ya está en uso", etc.
        let mensaje;
        if (campos.length === 1) {
          mensaje = `Ese ${campos[0]} ya está en uso.`;
        } else {
          mensaje = `Esos ${campos.join(' y ')} ya están en uso.`;
        }
        modal.open(
          'Datos en uso',
          `<p style="margin: 0;">${mensaje}</p>`,
          [{ text: 'Entendido', class: 'btn-primary' }]
        );
      } else if (error.status === 409) {
        modal.open(
          'No se puede guardar',
          `<p style="margin: 0;">${error.message || 'Conflicto al guardar.'}</p>`,
          [{ text: 'Entendido', class: 'btn-primary' }]
        );
      } else {
        window.toast.error(error.message || 'Error al guardar');
      }
    } finally {
      btnGuardar.disabled = false;
      btnGuardar.textContent = this.data.isEditing ? 'Guardar Cambios' : 'Crear Persona';
    }
  },

  recopilarDatos() {
    const tipoPersona = document.getElementById('tipoPersona').value;

    const data = {
      tipo_persona: tipoPersona,
      nombre_completo: document.getElementById('inputNombreCompleto').value.trim(),
      nombre_comercial: document.getElementById('inputNombreComercial').value.trim() || null,
      rfc: document.getElementById('inputRfc').value.trim().toUpperCase() || null,
      email: document.getElementById('inputEmail').value.trim() || null,
      telefono: document.getElementById('inputTelefono').value.trim() || null,
      celular: document.getElementById('inputCelular').value.trim() || null,
      whatsapp: document.getElementById('inputWhatsapp').value.trim() || null,
      direccion: document.getElementById('inputDireccion').value.trim() || null,
      colonia: document.getElementById('inputColonia').value.trim() || null,
      ciudad: document.getElementById('inputCiudad').value.trim() || null,
      estado: document.getElementById('inputEstado').value || null,
      codigo_postal: document.getElementById('inputCodigoPostal').value.trim() || null,
      notas: document.getElementById('inputNotas').value.trim() || null
    };

    // Solo incluir 'activo' en edición, no en creación
    if (this.data.isEditing) {
      data.activo = document.getElementById('inputActivo').value === '1';
    }

    // Detalles de persona física
    if (tipoPersona === 'fisica') {
      data.persona_fisica = {
        nombre: document.getElementById('inputPfNombre').value.trim(),
        apellido_paterno: document.getElementById('inputPfApellidoPaterno').value.trim(),
        apellido_materno: document.getElementById('inputPfApellidoMaterno').value.trim() || null,
        curp: document.getElementById('inputPfCurp').value.trim().toUpperCase() || null,
        fecha_nacimiento: document.getElementById('inputPfFechaNacimiento').value || null,
        tipo_identificacion: document.getElementById('inputPfTipoId').value || null,
        numero_identificacion: document.getElementById('inputPfNumeroId').value.trim() || null
      };
    }

    // Detalles de persona moral
    if (tipoPersona === 'moral') {
      data.persona_moral = {
        razon_social: document.getElementById('inputPmRazonSocial').value.trim(),
        fecha_constitucion: document.getElementById('inputPmFechaConstitucion').value || null,
        representante_legal: document.getElementById('inputPmRepresentante').value.trim() || null,
        email_representante: document.getElementById('inputPmEmailRepresentante').value.trim() || null,
        telefono_representante: document.getElementById('inputPmTelefonoRepresentante').value.trim() || null,
        regimen_fiscal: document.getElementById('inputPmRegimenFiscal').value.trim() || null
      };
    }

    // En edición CON rol preseleccionado (?rol=cliente, etc.) la sección "Roles"
    // está oculta — no enviar el campo. Si se está en vista genérica (sin filtro)
    // sí se envían los roles del checkbox (aunque el backend de actualizar()
    // actualmente los ignora; la gestión de roles se hace por endpoints aparte).
    if (this.data.isEditing && this.data.rolPreseleccionado) {
      return data;
    }

    // Roles seleccionados
    // Verificar si hay un rol preseleccionado (input hidden) o checkboxes
    const rolPreseleccionadoInput = document.getElementById('rolPreseleccionado');
    const rolesCheckboxes = document.querySelectorAll('input[name="roles"]:checked');

    if (rolPreseleccionadoInput) {
      // Caso: rol preseleccionado desde URL
      const rolData = { rol: rolPreseleccionadoInput.value };

      // Si es responsable, agregar configuración
      if (rolPreseleccionadoInput.value === 'responsable') {
        const puestoEl = document.getElementById('inputResponsablePuesto');
        const areaEl = document.getElementById('inputResponsableArea');
        const firmaEl = document.getElementById('inputPuedeFirmarLiberaciones');
        const otEl = document.getElementById('inputPuedeAprobarOT');

        rolData.configuracion_rol = {
          puesto: puestoEl?.value.trim() || null,
          area: areaEl?.value || null,
          puede_firmar_liberaciones: firmaEl?.checked || false,
          puede_aprobar_ot: otEl?.checked || false
        };
      }

      data.roles = [rolData];
    } else {
      // Caso: selección múltiple de roles via checkboxes
      data.roles = Array.from(rolesCheckboxes).map(checkbox => {
        const rolData = { rol: checkbox.value };

        // Si es responsable, agregar configuración
        if (checkbox.value === 'responsable') {
          const puestoEl = document.getElementById('inputResponsablePuesto');
          const areaEl = document.getElementById('inputResponsableArea');
          const firmaEl = document.getElementById('inputPuedeFirmarLiberaciones');
          const otEl = document.getElementById('inputPuedeAprobarOT');

          rolData.configuracion_rol = {
            puesto: puestoEl?.value.trim() || null,
            area: areaEl?.value || null,
            puede_firmar_liberaciones: firmaEl?.checked || false,
            puede_aprobar_ot: otEl?.checked || false
          };
        }

        return rolData;
      });
    }

    return data;
  }
};
